import { exportToCsv } from "@/routes/manage/-internal/ValidationStep/exportToCsv";
import { AuthState } from "@/services/auth/auth";
import { fetchData, FetchError } from "@/services/graphql/queryUtil";
import {
  BatchGetPresetElementsDocument,
  BatchGetPresetElementsQuery,
  BatchGetPresetElementsQueryVariables,
  BatchGetPublicPresetElementsDocument,
  BatchGetPublicPresetElementsQuery,
  BatchGetPublicPresetElementsQueryVariables,
  GetElementQuery,
  GetElementQueryVariables,
  GetInstitutionByCharterNumberDocument,
  GetInstitutionByCharterNumberQuery,
  GetInstitutionByCharterNumberQueryVariables,
  GetPoolsDocument,
  GetPoolsQuery,
  GetPoolsQueryVariables,
  GetPresetElementQuery,
  GetPresetElementQueryVariables,
  GetPublicPresetElementQuery,
  GetPublicPresetElementQueryVariables,
  GetRemittanceHistoriesByClientAndRoleDocument,
  GetRemittanceHistoriesByClientAndRoleQuery,
  GetRemittanceHistoriesByClientAndRoleQueryVariables,
  PoolRole,
  PoolsForBuyerDocument,
  PoolsForBuyerQuery,
  PoolsForBuyerQueryVariables,
  RemittanceStatus,
  Report_ElementStatus,
  Report_ValueType,
  useBatchGetPresetElementsQuery,
  useBatchGetPublicPresetElementsQuery,
  useGetElementQuery,
  useGetInstitutionByCharterNumberQuery,
  useGetPoolsQuery,
  useGetPresetElementQuery,
  useGetPublicPresetElementQuery,
  usePoolsForBuyerQuery,
} from "@/services/graphql/reactQuery";
import { getDisplayForCell } from "@/shared/components/elements/util";
import { PaxTableCell, RefetchControl, ReportElementData, RowClass } from "@/shared/types/element";
import { ChipProps, PaxChip, SxProps } from "@ncino/pax-ui";
import { QueryClient, QueryState, UseQueryResult } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { MutableRefObject, ReactNode } from "react";
import {
  LoanPoolItem,
  ManagedPoolAction,
  ManagedPoolActionName,
  PurchasedPool,
  RowActionMap,
  SharedPoolSchema,
  SoldPool,
  TableHeaders,
} from "./dataTypes";
import { TFunction } from "i18next";
import { EyeIcon, ReceiptIcon, CloudArrowUpIcon, XCircleIcon } from "@phosphor-icons/react";

interface FetchPoolsContext {
  queryClient: QueryClient;
  auth: AuthState;
}
export const presetElementRequestIdWithPoolId = (presetElementKey: string, poolId: string) =>
  `${presetElementKey}|${poolId}`;

export function fetchSoldPools(institutionId: number, context: FetchPoolsContext) {
  return context.queryClient.fetchQuery<GetPoolsQuery>({
    queryKey: useGetPoolsQuery.getKey({ clientId: institutionId }),
    queryFn: () =>
      fetchData<GetPoolsQuery, GetPoolsQueryVariables>(
        GetPoolsDocument,
        context.auth.tokenProvider
      )({ clientId: institutionId, isActiveOnly: true }),
  });
}

export function fetchPurchasedPools(institutionId: number, context: FetchPoolsContext) {
  return context.queryClient.fetchQuery<PoolsForBuyerQuery>({
    queryKey: usePoolsForBuyerQuery.getKey({ buyerId: institutionId }),
    queryFn: () =>
      fetchData<PoolsForBuyerQuery, PoolsForBuyerQueryVariables>(
        PoolsForBuyerDocument,
        context.auth.tokenProvider
      )({ buyerId: institutionId }),
  });
}

export function fetchRemittancePools(
  institutionId: number,
  role: PoolRole,
  context: FetchPoolsContext
) {
  return context.queryClient.fetchQuery<GetRemittanceHistoriesByClientAndRoleQuery>({
    queryKey: useGetPoolsQuery.getKey({ clientId: institutionId }),
    queryFn: () =>
      fetchData<
        GetRemittanceHistoriesByClientAndRoleQuery,
        GetRemittanceHistoriesByClientAndRoleQueryVariables
      >(
        GetRemittanceHistoriesByClientAndRoleDocument,
        context.auth.tokenProvider
      )({ clientId: institutionId, role: role }),
  });
}

export function queryStatusToReportStatus(status: "error" | "success" | "pending") {
  switch (status) {
    case "error":
      return Report_ElementStatus.Failed;
    case "success":
      return Report_ElementStatus.Completed;
    case "pending":
      return Report_ElementStatus.Processing;
  }
}

export function useManagePurchasedPools() {
  const { institutionId } = useRouteContext({ from: "__root__" });
  const { data, isLoading, isError, isSuccess, status, refetch, error, isRefetching } =
    usePoolsForBuyerQuery({ buyerId: institutionId }, { refetchOnWindowFocus: false });

  const purchasedPoolData =
    isSuccess && !isLoading
      ? data.poolParticipants.filter((participant) => participant.isSeller === false)
      : [];
  const reportStatus = queryStatusToReportStatus(status);

  return {
    data: purchasedPoolData as PurchasedPool[],
    isLoading,
    isError,
    reportStatus,
    refetch,
    error,
    isRefetching,
  };
}

export function useManageSoldPools() {
  const { institutionId } = useRouteContext({ from: "__root__" });
  const { data, isLoading, isError, isSuccess, status, refetch, error, isRefetching } =
    useGetPoolsQuery(
      { clientId: institutionId, isActiveOnly: true },
      { refetchOnWindowFocus: false }
    );

  const soldPoolData =
    isSuccess && !isLoading
      ? data.pools.filter(
          (pool) => pool.participants?.find((p) => p.buyerId === institutionId)?.isSeller
        )
      : [];
  const reportStatus = queryStatusToReportStatus(status);

  return {
    data: soldPoolData as SoldPool[],
    isLoading,
    isError,
    reportStatus,
    refetch,
    error,
    isRefetching,
  };
}

export function getPoolBadgeColor(status: RemittanceStatus) {
  switch (status) {
    case RemittanceStatus.Acknowledged:
    case RemittanceStatus.Complete:
    case RemittanceStatus.CompletedBySystem:
      return "success";
    case RemittanceStatus.ReadyToReview:
    case RemittanceStatus.InReview:
      return "warning";
    case RemittanceStatus.Cancelled:
      return "error";
    case RemittanceStatus.AwaitingData:
      return "primary";
    case RemittanceStatus.DataPending:
    default:
      return "default";
  }
}

export function makePoolBadge(label: RemittanceStatus, t: TFunction<"manage", undefined>) {
  const trueLabel = translateRemittanceStatus(label as RemittanceStatus, t);
  const clr = getPoolBadgeColor(label);
  return <PaxChip label={trueLabel} color={clr} variant="soft" />;
}

export function getActionByName(actionName: ManagedPoolActionName): keyof RowActionMap {
  switch (actionName) {
    case "View Pool Details":
      return "openTab";
    case "Create Remittance":
      return "openRemittanceModal";
    case "Review Remittance":
      return "openModal";
    case "Load Data":
      return "openLoadTransactionDataModal";
    case "Cancel Remittance":
      return "openCancelRemittanceModal";
    default:
      return "openModal";
  }
}

// COMPLETED is pending backend codegen addition; the default case handles it correctly now.
export const RemittanceStatusCompleted = "COMPLETED" as const;

interface ActionPermissions {
  canReviewRemittance: boolean;
  canLoadData: boolean;
}

export function getAvailableManagedPoolActions(
  status: RemittanceStatus | typeof RemittanceStatusCompleted,
  isSeller: boolean,
  actionMap: RowActionMap,
  permissions: ActionPermissions = { canReviewRemittance: true, canLoadData: true }
): ManagedPoolAction[] {
  const viewPoolDetails: Partial<ManagedPoolAction> = {
    name: "View Pool Details",
    icon: <EyeIcon />,
  };
  const loadData: Partial<ManagedPoolAction> = { name: "Load Data", icon: <CloudArrowUpIcon /> };
  const reviewRemittance: Partial<ManagedPoolAction> = {
    name: "Review Remittance",
    icon: <ReceiptIcon />,
  };
  const cancelRemittance: Partial<ManagedPoolAction> = {
    name: "Cancel Remittance",
    icon: <XCircleIcon />,
  };

  let partialActions: Partial<ManagedPoolAction>[];

  if (isSeller) {
    switch (status) {
      case RemittanceStatus.AwaitingData:
      case RemittanceStatus.DataPending:
      case RemittanceStatus.Cancelled:
        partialActions = [viewPoolDetails, loadData];
        break;
      case RemittanceStatus.ReadyToReview:
        partialActions = [viewPoolDetails, reviewRemittance, cancelRemittance];
        break;
      case RemittanceStatus.InReview:
        // Seller may reload corrected data during review; status stays IN_REVIEW
        partialActions = [viewPoolDetails, reviewRemittance, loadData, cancelRemittance];
        break;
      default:
        // Covers ACKNOWLEDGED and COMPLETED: remittance is locked, no further seller actions
        partialActions = [viewPoolDetails];
    }
  } else {
    // Buyer only sees remittance actions once seller has acknowledged
    switch (status) {
      case RemittanceStatus.Acknowledged:
        partialActions = [viewPoolDetails, reviewRemittance];
        break;
      default:
        partialActions = [viewPoolDetails];
    }
  }

  const visibleActions = partialActions.filter((a) => {
    if (a.name === "Review Remittance") return permissions.canReviewRemittance;
    if (a.name === "Load Data") return permissions.canLoadData;
    return true;
  });

  const availableActions = visibleActions.map((a) => {
    const actionKey = getActionByName(a.name!);

    let onClick;
    if (actionKey === "openRemittanceModal") {
      onClick = (loanPool: LoanPoolItem) => actionMap.openRemittanceModal(loanPool);
    } else if (actionKey === "openLoadTransactionDataModal") {
      onClick = (loanPool: LoanPoolItem) => actionMap.openLoadTransactionDataModal(loanPool);
    } else if (actionKey === "openCancelRemittanceModal") {
      onClick = (loanPool: LoanPoolItem) => actionMap.openCancelRemittanceModal(loanPool);
    } else if (actionKey === "openModal") {
      onClick = (loanPool: LoanPoolItem) => actionMap.openModal(loanPool);
    } else {
      onClick = (loanPool: LoanPoolItem) => actionMap.openTab(loanPool, true);
    }

    return {
      name: a.name,
      icon: a.icon,
      onClick,
    };
  });

  return availableActions as ManagedPoolAction[];
}

export { RemittanceStatus };

export function getPoolItem(row: SharedPoolSchema): LoanPoolItem {
  return {
    name: row.poolName,
    id: row.poolId,
    origin: row.origin,
    tabId: `${row.origin}-${row.poolId}`,
    count: row.numLoans,
    status: row.status,
    reportingPeriod: row.reportingPeriod,
  };
}

export function getPoolOriginChip({ origin }: Pick<LoanPoolItem, "origin">) {
  const sx: SxProps = {
    textTransform: "capitalize",
    marginLeft: "10px",
  };
  const props: ChipProps = {
    variant: "soft",
    size: "small",
    sx: {
      textTransform: "capitalize",
      marginLeft: "5px",

      "& .MuiChip-label": {
        typography: "caption",
      },
    },
  };
  switch (origin) {
    case "Purchased":
      return <PaxChip {...props} label="Purchased" color="warning" />;
    case "Sold":
      return <PaxChip {...props} label="Sold" color="secondary" />;
  }
}

export interface PollingUpdate {
  pollingAttempts: number;
  pollingIntervalMs: number;
}

// Add a short buffer to when checking for timeout to avoid error state flickering
const POLLING_TIMEOUT_BUFFER = 3000;

export const BASE_POLLING_SCHEDULE: PollingUpdate[] = [
  {
    // 0-10s
    pollingAttempts: 10, // Try 10 times
    pollingIntervalMs: 1000, // Using an initial polling interval of 1 second
  },
  {
    // 10-60s
    pollingAttempts: 10,
    pollingIntervalMs: 5000,
  },
  {
    // 60s - 300s
    pollingAttempts: 8,
    pollingIntervalMs: 30000,
  },
];

export function pollingInterval(
  queryState: QueryState<GetElementQuery, unknown> | QueryState<GetPresetElementQuery | unknown>,
  pollingSchedule: PollingUpdate[]
): number | false {
  // initial fetch (0s) is updateCount 1, subtracting 1 so updateCount matches polling attempts made.
  const updateCount = queryState.dataUpdateCount - 1;
  let totalAttempts = 0;

  for (const schedule of pollingSchedule) {
    totalAttempts += schedule.pollingAttempts;
    if (updateCount < totalAttempts) {
      return schedule.pollingIntervalMs;
    }
  }

  return false;
}

export const isTerminalStatus = (status: Report_ElementStatus | undefined) =>
  status === Report_ElementStatus.Completed ||
  status === Report_ElementStatus.Failed ||
  status === undefined;

export type ElementDataOptions = {
  /**
   * Set this when the surrounding component also calls one of the batch hooks, which request every
   * element in a single round trip and write each result into the individual query's cache entry.
   * The individual query then stays disabled and reads its element out of that cache.
   */
  batched?: boolean;
  pollingSchedule?: PollingUpdate[];
};

/** How long an element may sit non-terminal before it is reported as timed out. */
export const pollingTimeout = (pollingSchedule: PollingUpdate[]): number =>
  (pollingSchedule.length > 0 ? pollingSchedule[pollingSchedule.length - 1].pollingIntervalMs : 0) +
  POLLING_TIMEOUT_BUFFER;

export function useBatchPresetData(
  variables: BatchGetPresetElementsQueryVariables,
  pollingSchedule: PollingUpdate[] = BASE_POLLING_SCHEDULE
): void {
  const { auth, queryClient } = useRouteContext({ from: "__root__" });

  // split out request ids from other variables for caching individual queries
  const { requestIds: _, ...seedVars } = variables;
  const requestIds = Array.isArray(variables.requestIds)
    ? variables.requestIds
    : [variables.requestIds];

  const res = useBatchGetPresetElementsQuery(variables, {
    queryFn: () => {
      const fetchList = requestIds.filter((requestId) => {
        const query = queryClient.getQueryCache().find<GetPresetElementQuery>({
          queryKey: useGetPresetElementQuery.getKey({ requestId, ...seedVars }),
        });
        const data = query?.state?.data;

        // staleness is handled per observer, and may be a function
        const isStale =
          query?.getObserversCount() === 0 ||
          query?.observers.some((observer) => {
            const res = observer.getCurrentResult();
            const staleTime =
              typeof observer.options.staleTime === "function"
                ? observer.options.staleTime(observer.getCurrentQuery())
                : (observer.options.staleTime ?? 0);
            return (
              res.isStale ||
              (typeof staleTime === "number" && Date.now() - res.dataUpdatedAt > staleTime)
            );
          });

        // check if data for individual queries exists, if it's stale, and if it's still processing
        return (
          isStale ||
          !data?.report_getPresetElement ||
          !isTerminalStatus(data.report_getPresetElement.status)
        );
      });

      return fetchData<BatchGetPresetElementsQuery, BatchGetPresetElementsQueryVariables>(
        BatchGetPresetElementsDocument,
        auth.tokenProvider
      )({ requestIds: fetchList, ...seedVars });
    },
    refetchInterval: (query) => {
      const elements = (query.state.data as BatchGetPresetElementsQuery | undefined)
        ?.report_batchGetPresetElements;
      if (!elements) return false;
      for (const element of elements) {
        if (!isTerminalStatus(element?.status)) {
          return pollingInterval(query.state, pollingSchedule);
        }
      }
      return false;
    },
  });

  const elements = res.data?.report_batchGetPresetElements;

  for (const requestId of requestIds) {
    if (elements) {
      const el = elements.find((element) => element?.requestId === requestId);
      if (el)
        queryClient.setQueryData(
          useGetPresetElementQuery.getKey({ requestId, ...seedVars }),
          () => {
            return { report_getPresetElement: el };
          }
        );
    }
  }
}

export function useElementData(
  variables: GetElementQueryVariables,
  pollingSchedule = BASE_POLLING_SCHEDULE
): ReportElementData & RefetchControl {
  const result = useGetElementQuery(variables, {
    refetchInterval: (query) => {
      const status = (query.state.data as GetElementQuery | undefined)?.report_getElement?.status;
      return isTerminalStatus(status) ? false : pollingInterval(query.state, pollingSchedule);
    },
  });
  const element = result.data?.report_getElement as ReportElementData | undefined | null;

  return resolveElementQuery({
    result,
    element,
    requestId: variables.requestId ?? "",
    timeout: pollingTimeout(pollingSchedule),
  });
}

export function resolveElementQuery({
  result,
  element,
  requestId,
  timeout,
}: {
  result:
    | UseQueryResult<NoInfer<GetElementQuery>, FetchError>
    | UseQueryResult<NoInfer<GetPresetElementQuery>, FetchError>
    | UseQueryResult<NoInfer<GetPublicPresetElementQuery>, FetchError>;
  element: ReportElementData | undefined | null;
  requestId: string;
  timeout: number;
}): ReportElementData & RefetchControl {
  const nullResponse = result.isFetched && element === null;
  const refetchSettings: RefetchControl = {
    refetchControl: {
      refetch: result.refetch,
      isRetryableError: !result.isLoading && !!result.error?.canRetry,
      isRefetching: result.isRefetching,
    },
  };

  if (result.isError || nullResponse || element?.status === Report_ElementStatus.Failed) {
    const backendErrors = element?.error;
    const errorDescription = result.isError
      ? "Failed to fetch element data. Please try again."
      : nullResponse
        ? "Element data unavailable."
        : Array.isArray(backendErrors) && backendErrors.length
          ? backendErrors.join("; ")
          : "Element processing failed.";
    return {
      requestId: requestId,
      truncated: null,
      status: Report_ElementStatus.Failed,
      error: errorDescription,
      ...refetchSettings,
    };
  }
  if (
    !element ||
    element.status == Report_ElementStatus.Queued ||
    element.status === Report_ElementStatus.Processing
  ) {
    if (result.dataUpdatedAt && Date.now() - result.dataUpdatedAt > timeout) {
      return {
        requestId: requestId,
        status: Report_ElementStatus.Failed,
        truncated: null,
        error: "Request Timed Out. Please try again later.",
        refetchControl: {
          ...refetchSettings.refetchControl,
          isRetryableError: true,
        },
      };
    }
    return {
      requestId: requestId,
      status: Report_ElementStatus.Processing,
      chartData: [],
      tableData: { headers: [], rows: [] },
      truncated: null,
      error: null,
      ...refetchSettings,
    };
  }
  return { ...element, ...refetchSettings };
}

export function usePublicPresetElementData(
  variables: GetPublicPresetElementQueryVariables,
  { batched = false, pollingSchedule = BASE_POLLING_SCHEDULE }: ElementDataOptions = {}
): ReportElementData & RefetchControl {
  const result = useGetPublicPresetElementQuery(variables, {
    refetchInterval: (query) => {
      const status = (query.state.data as GetPublicPresetElementQuery | undefined)
        ?.report_getPublicPresetElement?.status;
      return isTerminalStatus(status) ? false : pollingInterval(query.state, pollingSchedule);
    },
    enabled: !batched,
  });
  const element = result.data?.report_getPublicPresetElement as
    | ReportElementData
    | undefined
    | null;

  return resolveElementQuery({
    result,
    element,
    requestId: `${variables.elementKey}|${variables.poolId}`,
    timeout: pollingTimeout(pollingSchedule),
  });
}

export function useBatchPublicPresetData(
  variables: BatchGetPublicPresetElementsQueryVariables,
  pollingSchedule: PollingUpdate[] = BASE_POLLING_SCHEDULE
): void {
  const { auth, queryClient } = useRouteContext({ from: "__root__" });

  // split out request ids from other variables for caching individual queries
  const { elementKeys: _, ...seedVars } = variables;
  const elementKeys = Array.isArray(variables.elementKeys)
    ? variables.elementKeys
    : [variables.elementKeys];

  const res = useBatchGetPublicPresetElementsQuery(variables, {
    queryFn: () => {
      const fetchList = elementKeys.filter((elementKey) => {
        const query = queryClient.getQueryCache().find<GetPublicPresetElementQuery>({
          queryKey: useGetPublicPresetElementQuery.getKey({ elementKey, ...seedVars }),
        });
        const data = query?.state?.data;

        // staleness is handled per observer, and may be a function
        const isStale =
          query?.getObserversCount() === 0 ||
          query?.observers.some((observer) => {
            const res = observer.getCurrentResult();
            const staleTime =
              typeof observer.options.staleTime === "function"
                ? observer.options.staleTime(observer.getCurrentQuery())
                : (observer.options.staleTime ?? 0);
            return (
              res.isStale ||
              (typeof staleTime === "number" && Date.now() - res.dataUpdatedAt > staleTime)
            );
          });

        // check if data for individual queries exists, if it's stale, and if it's still processing
        return (
          isStale ||
          !data?.report_getPublicPresetElement ||
          !isTerminalStatus(data.report_getPublicPresetElement.status)
        );
      });

      return fetchData<
        BatchGetPublicPresetElementsQuery,
        BatchGetPublicPresetElementsQueryVariables
      >(
        BatchGetPublicPresetElementsDocument,
        auth.tokenProvider
      )({ elementKeys: fetchList, ...seedVars });
    },
    refetchInterval: (query) => {
      const elements = (query.state.data as BatchGetPublicPresetElementsQuery | undefined)
        ?.report_batchGetPublicPresetElements;
      if (!elements) return false;
      for (const element of elements) {
        if (!isTerminalStatus(element?.status)) {
          return pollingInterval(query.state, pollingSchedule);
        }
      }
      return false;
    },
  });

  const elements = res.data?.report_batchGetPublicPresetElements;

  for (const elementKey of elementKeys) {
    if (elements) {
      const el = elements.find(
        (element) => element?.requestId === `${elementKey}|${variables.poolId}`
      );
      if (el)
        queryClient.setQueryData(
          useGetPublicPresetElementQuery.getKey({ elementKey, ...seedVars }),
          () => {
            return { report_getPublicPresetElement: el };
          }
        );
    }
  }
}

export function usePresetElementData(
  variables: GetPresetElementQueryVariables,
  { batched = false, pollingSchedule = BASE_POLLING_SCHEDULE }: ElementDataOptions = {}
): ReportElementData & RefetchControl {
  const result = useGetPresetElementQuery(variables, {
    refetchInterval: (query) => {
      const status = (query.state.data as GetPresetElementQuery | undefined)
        ?.report_getPresetElement?.status;
      return isTerminalStatus(status) ? false : pollingInterval(query.state, pollingSchedule);
    },
    enabled: !batched,
  });
  const element = result.data?.report_getPresetElement as ReportElementData | undefined | null;

  return resolveElementQuery({
    result,
    element,
    requestId: variables.requestId ?? "",
    timeout: pollingTimeout(pollingSchedule),
  });
}

export function boxTitleFromData(
  data: ReportElementData | undefined,
  t: (key: string) => string
): string {
  return data?.tableData?.rows?.[0]?.cells?.[0]?.displayValue
    ? (data?.tableData?.rows?.[0]?.cells?.[0]?.displayValue ?? "")
    : (data?.tableData?.rows?.[0]?.cells?.[0]?.value ?? "");
}

export function categoryLabelsFromData(data: ReportElementData | undefined): string[] {
  return (
    data?.tableData?.rows
      ?.filter(
        (row) =>
          ![
            RowClass.TOTAL,
            RowClass.SUBHEADER,
            RowClass.SUBTOTAL,
            RowClass.SUBTOTAL_ALTERNATE,
          ].includes(row.rowClass as RowClass)
      )
      .map((row) => row.cells[0]?.displayValue ?? row.cells[0]?.value ?? "")
      .filter(Boolean) ?? []
  );
}

export function poolsToElementData<T>(pools: T[], headers: TableHeaders<T>): ReportElementData {
  if (pools.length === 0 && headers.length > 0) {
    return {
      requestId: "",
      status: "COMPLETED",
      tableData: {
        headers: headers.map(({ displayName, sortable }) => ({
          displayName,
          sortable,
        })),
        rows: [],
      },
      truncated: null,
      error: undefined,
    };
  } else if (pools.length === 0) {
    return {
      requestId: "",
      status: "COMPLETED",
      tableData: { headers: [], rows: [] },
      truncated: null,
      error: undefined,
    };
  }

  return {
    requestId: "",
    status: "COMPLETED",
    truncated: false,
    error: null,
    tableData: {
      headers: headers.map(({ displayName, sortable }) => ({
        displayName,
        sortable,
      })),
      rows: pools.map((pool) => ({
        cells: headers.map(({ columnKey, converter }): PaxTableCell => {
          const raw = pool[columnKey as keyof T];
          const isEmpty = raw == null || raw === "";
          const value = isEmpty ? "-" : String(raw);
          const valueType =
            columnKey === "price" ? Report_ValueType.Dollar : Report_ValueType.String;
          const converted =
            converter && !isEmpty
              ? (converter as (v: unknown, row: T) => ReactNode)(raw, pool)
              : undefined;
          const jsx =
            converted != null && typeof converted === "object"
              ? (converted as JSX.Element)
              : undefined;
          return jsx != null
            ? { jsx, value, displayValue: value, valueType: valueType }
            : { value, displayValue: value, valueType: valueType };
        }),
      })),
    },
  };
}

export { getDataDatesFromSellDate, getEndOfMonth } from "../../../shared/utility/dateUtils";

export function elementDataToCsv(fileName: string, elementData: ReportElementData) {
  if (!elementData.tableData) return;

  const csvHeaders = elementData.tableData.headers.map((header) => header.displayName);

  const csvRows =
    elementData.tableData.rows?.map((row) => {
      return row.cells.map((cell) => getDisplayForCell(cell));
    }) ?? [];

  exportToCsv(fileName, csvHeaders, csvRows);
}

export function poolNameIsValid(name: string, MAX_NAME_COUNT: number) {
  if (name) {
    return name.length <= MAX_NAME_COUNT;
  }
  return true;
}
export const VALID_PRECISION = 6;

export function numberPrecisionIsValid(num: number | string, inclusivePrecision: number) {
  if (num == null) {
    return false;
  }

  let copy = num;
  if (typeof copy === "number") {
    copy = copy.toString();
  }
  if (!copy.includes(".")) return true;
  const splitCopy = copy.split(".");
  return splitCopy[1].length <= inclusivePrecision;
}

export function setupInterval(
  intervalRef: MutableRefObject<ReturnType<typeof setInterval> | null>,
  onInterval: () => void,
  timer: number
) {
  if (intervalRef.current !== null) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }

  intervalRef.current = setInterval(() => {
    onInterval();
  }, timer);
}

export function cleanupInterval(
  intervalRef: MutableRefObject<ReturnType<typeof setInterval> | null>,
  isDonePredicate: () => boolean
) {
  if (isDonePredicate() && intervalRef.current !== null) {
    clearInterval(intervalRef.current);
  }
}

export function isReportElementQueryDone(status: Report_ElementStatus | undefined) {
  if (status == undefined) {
    return false;
  }

  switch (status) {
    case Report_ElementStatus.Completed:
    case Report_ElementStatus.Failed:
      return true;
  }
  return false;
}

export function translateRemittanceStatus(
  remittanceStatus: RemittanceStatus | null | undefined,
  t: TFunction<"manage", undefined>
): string {
  switch (remittanceStatus) {
    case RemittanceStatus.Complete:
      return t("remittanceStatus.complete");
    case RemittanceStatus.InReview:
      return t("remittanceStatus.inReview");
    case RemittanceStatus.DataPending:
      return t("remittanceStatus.dataPending");
    case RemittanceStatus.CompletedBySystem:
      return t("remittanceStatus.completedBySystem");
    case RemittanceStatus.AwaitingData:
      return t("remittanceStatus.awaitingData");
    case RemittanceStatus.Acknowledged:
      return t("remittanceStatus.acknowledged");
    case RemittanceStatus.Cancelled:
      return t("remittanceStatus.cancelled");
    case RemittanceStatus.ReadyToReview:
    default:
      return t("remittanceStatus.readyToReview");
  }
}

export function fetchGetInstitutionByCharterQuery(
  context: FetchPoolsContext,
  institutionId: number,
  charterNumbers: number[]
) {
  const variables = {
    institutionId: institutionId,
    charterNumbers: charterNumbers,
  };

  return context.queryClient.fetchQuery<GetInstitutionByCharterNumberQuery>({
    queryKey: useGetInstitutionByCharterNumberQuery.getKey(variables),
    queryFn: () =>
      fetchData<GetInstitutionByCharterNumberQuery, GetInstitutionByCharterNumberQueryVariables>(
        GetInstitutionByCharterNumberDocument,
        context.auth.tokenProvider
      )(variables),
  });
}
